import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { notificationService, courseService } from '../services/api';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const { user } = useUser(); 
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNominationPopup, setShowNominationPopup] = useState(false);
    const [currentNominationId, setCurrentNominationId] = useState(null);
    const initialMount = React.useRef(true);

    // Add feedback modal state
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [feedbackData, setFeedbackData] = useState(null);

    const fetchData = React.useCallback(async () => {
        //if (!user || !initialMount.current) return;
        if (!user) return;
        //initialMount.current = false;
        try {
            const [notificationsRes, countRes] = await Promise.all([
                notificationService.getLatestNotifications(),
                notificationService.getUnreadCount()
            ]);
            if (Array.isArray(notificationsRes.data)) {
                setNotifications(notificationsRes.data);
            } else if (notificationsRes.data && notificationsRes.data.notifications) {
                setNotifications(notificationsRes.data.notifications);
            } else {
                setNotifications([]);
            }
            if (countRes.data && typeof countRes.data.count === 'number') {
                setUnreadCount(countRes.data.count);
            } else if (typeof countRes.data === 'number') {
                setUnreadCount(countRes.data);
            } else {
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    }, [user]);

    useEffect(() => {
        let intervalId;
        
        if (user) {
            fetchData();
            //intervalId = setInterval(fetchData, 60000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
        
    }, [user, fetchData]);

    const approveRequest = async (notificationId, courseId, learnerId) => {
        try {
            await notificationService.approve(notificationId, courseId, learnerId);
            fetchData(); // Refresh notifications
            return true;
        } catch (error) {
            console.error('Error approving request:', error);
            return false;
        }
    };

    const rejectRequest = async (notificationId, courseId, learnerId) => {
        try {
            await notificationService.reject(notificationId, courseId, learnerId);
            fetchData(); // Refresh notifications
            return true;
        } catch (error) {
            console.error('Error rejecting request:', error);
            return false;
        }
    };

    const acceptNomination = async (nominationId) => {
        try {
            await courseService.acceptNomination(nominationId);
            await fetchData();
            return true;
        } catch (error) {
            console.error('Error accepting nomination:', error);
            return false;
        }
    };

    const rejectNomination = async (nominationId) => {
        try {
            await courseService.rejectNomination(nominationId);
            await fetchData();
            return true;
        } catch (error) {
            console.error('Error rejecting nomination:', error);
            return false;
        }
    };

    const openNominationPopup = (nominationId) => {
        setCurrentNominationId(nominationId);
        setShowNominationPopup(true);
    };

    const closeNominationPopup = () => {
        setShowNominationPopup(false);
        setCurrentNominationId(null);
        fetchData();
    };

    const markAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);//await axios.post(`/api/notifications/mark-read/${notificationId}`);
          fetchData();
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
    };

    // Add functions to handle feedback popup
    const openFeedbackPopup = (itemId, enrollmentId, instanceId, itemType) => {
        setFeedbackData({
            itemId,
            enrollmentId,
            instanceId,
            itemType
        });
        setShowFeedbackPopup(true);
    };

    const closeFeedbackPopup = (shouldRefresh = true) => {
        setShowFeedbackPopup(false);
        setFeedbackData(null);
        if (shouldRefresh) {
            fetchData(); // Refresh notifications after closing
        }
    };

    const handleNotificationClick = async (notification, event) => {
        // Check if the clicked element is a feedback link
        if (event.target.classList.contains('feedback-link')) {
            event.preventDefault();
            const itemId = event.target.getAttribute('data-item-id');
            const enrollmentId = event.target.getAttribute('data-enrollment-id');
            const instanceId = event.target.getAttribute('data-instance-id');
            const itemType = event.target.getAttribute('data-item-type');
            
            if (itemId && enrollmentId) {
                openFeedbackPopup(itemId, enrollmentId, instanceId, itemType);
            }
        }

        // Check if the clicked element is a nomination link
        if (event.target.classList.contains('nomination-link') || 
        event.target.classList.contains('acceptNomination') || 
        event.target.classList.contains('rejectNomination')) {
          event.preventDefault();
          const nominationId = event.target.getAttribute('data-nomination-id');
          if (nominationId) {
            if (event.target.classList.contains('acceptNomination')) {
                await acceptNomination(nominationId);
            } else if (event.target.classList.contains('rejectNomination')) {
                await rejectNomination(nominationId);
            } else {
                openNominationPopup(nominationId);
            }
          }
        }
        
        // Mark notification as read
        if (!notification.read_at) {
          markAsRead(notification.id);
        }
        // await fetchData();

    };
    
    return (
        <NotificationContext.Provider value={{ 
            notifications, 
            unreadCount,
            fetchData,
            approveRequest,
            rejectRequest,
            acceptNomination,
            rejectNomination,
            openNominationPopup,
            closeNominationPopup,
            showNominationPopup,
            currentNominationId,
            handleNotificationClick,
            markAsRead,
            showFeedbackPopup,
            feedbackData,
            openFeedbackPopup,
            closeFeedbackPopup
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
